import every from 'lodash/every';
import forEach from 'lodash/forEach';
import { getEventTransfer } from 'slate-react';
import { useCallback } from 'react';
import { Value } from 'slate';

import { isPastedFromExcel } from './excel/deserialize_excel';
import deserializeGdocs, { isPastedFromGdocs } from './gdocs/deserialize_gdocs';
import deserializeWord, { isPastedFromWord } from './word/deserialize_word';
import { insertFragmentWithoutExtraLineToStart } from '../plugins/fragment_helpers';
import PasteSerializer from 'components/text_editor/serializers/paste_serializer';

export function onPaste(event, editor, next) {
  event.preventDefault();
  const transfer = getEventTransfer(event);
  if (transfer.type !== 'html' || isPastedFromExcel(transfer.html)) return next();

  let fragment;
  const html = transfer.html;
  if (isPastedFromGdocs(html)) {
    fragment = deserializeGdocs(html);
  } else if (isPastedFromWord(html)) {
    fragment = deserializeWord(html);
  } else {
    const value = PasteSerializer.deserialize(html);
    const valueJSON = value.toJSON();
    wrapNonblockChildrenRuns(valueJSON.document);
    consolidateParagraphChildren(valueJSON.document);
    fragment = Value.fromJSON(valueJSON).document;
  }

  insertFragmentWithoutExtraLineToStart(editor, fragment);
}

export default function useOnPaste() {
  return useCallback(onPaste, []);
}

/**
 * This is a bit of a hack, but basically, slate does not like when we have blocks that have both block AND text/inline children.
 * So to get around this, if a block has "mixed" children, we will wrap all consecutive runs of text/inline children with a
 * block. E.g.:
 *
 * block
 *   block
 *     text1
 *   text2
 *   text3
 *
 * becomes
 *
 * block
 *   block
 *     text1
 *   block
 *     text2
 *     text3
 */
function wrapNonblockChildrenRuns(parent) {
  forEach(parent.nodes, node => {
    wrapNonblockChildrenRuns(node);
  });

  let fixedNodes = [];
  let run = [];
  forEach(parent.nodes, node => {
    if (node.object === 'block') {
      if (run.length) {
        fixedNodes.push({
          object: 'block',
          type: 'paragraph',
          nodes: run,
        });
        run = [];
      }
      fixedNodes.push(node);
    } else {
      run.push(node);
    }
  });
  if (run.length && fixedNodes.length) {
    fixedNodes.push({
      object: 'block',
      type: 'paragraph',
      nodes: run,
    });
    run = [];
  } else if (!fixedNodes.length) {
    fixedNodes = run;
  }

  parent.nodes = fixedNodes;
}

/**
 * After ensuring that our pasted content can actually be rendered by Slate (which enforces blocks not having mixed children),
 * we can, for blocks that only have block children that only have text nodes, remove the intermediate block and raise its children
 * up a level. E.g.
 *
 * block
 *   block
 *     text1
 *   block
 *     text2
 *
 * becomes
 *
 * block
 *   text1
 * block
 *   text2
 *
 * This is done recursively to flatten the block paragraph structure to the one that our slate editor outputs during normal editing
 * operations.
 *
 * While our slate editor _can_ handle unnecessarily nested block structures such as those created by `wrapNonblockChildrenRuns`, most of the
 * plugin coding was done with assumption that we'd have a mostly "flat" block paragraph structure (except for lists of course).
 * From what I can tell, nothing broke horrifically from nested blocks, but the behavior was sometimes inconsistent and weird, so
 * flattening is preferable.
 */
function consolidateParagraphChildren(parent) {
  forEach(parent.nodes, node => {
    consolidateParagraphChildren(node);
  });

  let nodes = [];
  forEach(parent.nodes, node => {
    if (onlyHasParagraphChildren(node)) {
      nodes = [...nodes, ...node.nodes];
    } else {
      nodes.push(node);
    }
  });
  parent.nodes = nodes;
}

function onlyHasParagraphChildren(node) {
  return (
    node.object === 'block' &&
    node.type === 'paragraph' &&
    node.nodes.length &&
    every(
      node.nodes,
      childNode =>
        childNode.object === 'block' &&
        childNode.type === 'paragraph' &&
        every(childNode.nodes, grandChild => grandChild.object === 'text')
    )
  );
}



// WEBPACK FOOTER //
// ./src/scripts/presentation/components/text_editor/paste/use_on_paste.js